import { useEffect,useMemo,useState } from 'react';
import { Link } from 'react-router-dom';
import { Award,BookOpen,CalendarDays,Flame,Medal,Sparkles,Target,Trophy } from 'lucide-react';
import { useLearner } from '../contexts';
import { getMastery,recentAttempts,totalXp } from '../lib/db';
import type { SubjectKey } from '../types/domain';

const subjectLabels:Record<SubjectKey,string>={
  reading:'Reading & English',writing:'Writing',maths:'Mathematics',science:'Science',
  humanities:'History & Geography',technology:'Technology & Engineering'
};
const subjectIcons:Record<SubjectKey,string>={
  reading:'📚',writing:'✍️',maths:'🔢',science:'🔬',humanities:'🌍',technology:'🤖'
};

function dayKey(value:string|Date){
  const d=new Date(value); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function currentStreak(rows:any[]){
  const days=new Set(rows.map(r=>dayKey(r.created_at)));
  let d=new Date(); let streak=0;
  if(!days.has(dayKey(d))){d.setDate(d.getDate()-1)}
  while(days.has(dayKey(d))){streak++;d.setDate(d.getDate()-1)}
  return streak;
}

export function Progress(){
  const {active}=useLearner();
  const [attempts,setAttempts]=useState<any[]>([]);
  const [mastery,setMastery]=useState<Record<string,number>>({});
  const [xp,setXp]=useState(0);
  useEffect(()=>{if(active){Promise.all([recentAttempts(active.id,120),getMastery(active.id),totalXp(active.id)]).then(([a,m,x])=>{setAttempts(a);setMastery(m);setXp(x)}).catch(()=>{})}},[active?.id]);
  const stats=useMemo(()=>{
    const today=dayKey(new Date());
    const todayRows=attempts.filter(a=>dayKey(a.created_at)===today);
    const todayMinutes=Math.round(todayRows.reduce((n,a)=>n+Number(a.duration_seconds||0),0)/60);
    const accuracy=attempts.length?Math.round(100*attempts.filter(a=>a.correct).length/attempts.length):0;
    const interests=new Set(attempts.flatMap(a=>a.interest_tags||[]));
    return {todayMinutes,accuracy,streak:currentStreak(attempts),interests:interests.size};
  },[attempts]);
  if(!active)return null;
  const awards=[
    {icon:'🌟',name:'First Step',detail:'Finish your first challenge',earned:attempts.length>=1},
    {icon:'🔥',name:'On a Roll',detail:'Build a 3-day learning streak',earned:stats.streak>=3},
    {icon:'🏆',name:'100 Point Club',detail:'Earn 100 Explorer Points',earned:xp>=100},
    {icon:'🧠',name:'Curious Mind',detail:'Complete 10 challenges',earned:attempts.length>=10},
    {icon:'🗺️',name:'World Explorer',detail:'Learn through 3 different interests',earned:stats.interests>=3},
    {icon:'🚀',name:'Super Explorer',detail:'Earn 500 Explorer Points',earned:xp>=500}
  ];
  const topSkills=Object.entries(mastery).sort((a,b)=>b[1]-a[1]).slice(0,8);
  return <div className="progress-page">
    <div className="page-heading"><span className="eyebrow">MY PROGRESS</span><h1>Look how far you’ve come.</h1><p>Every question, mission and discovery adds to your learning story.</p></div>
    <div className="progress-hero-grid">
      <div className="progress-stat"><Trophy/><b>{xp}</b><span>Explorer Points</span></div>
      <div className="progress-stat"><Flame/><b>{stats.streak}</b><span>day streak</span></div>
      <div className="progress-stat"><Target/><b>{attempts.length}</b><span>challenges completed</span></div>
      <div className="progress-stat"><CalendarDays/><b>{stats.todayMinutes}m</b><span>learning today</span></div>
    </div>
    <section className="section">
      <div className="section-title"><div><span className="eyebrow">MY SUBJECTS</span><h2>Your starting levels</h2></div></div>
      <div className="subject-launch-grid">{(Object.keys(active.subject_levels) as SubjectKey[]).map(k=><Link key={k} to={`/app/mission?subject=${k}`} className="subject-launch-card"><span>{subjectIcons[k]}</span><div><b>{subjectLabels[k]}</b><small>Grade {active.subject_levels[k]} starting point</small></div><BookOpen/></Link>)}</div>
    </section>
    <section className="section">
      <div className="section-title"><div><span className="eyebrow">AWARDS</span><h2>Things you’ve unlocked</h2></div><span className="award-count">{awards.filter(a=>a.earned).length}/{awards.length}</span></div>
      <div className="award-grid">{awards.map(a=><article key={a.name} className={a.earned?'award-card earned':'award-card'}><div>{a.earned?a.icon:'🔒'}</div><b>{a.name}</b><span>{a.detail}</span>{a.earned&&<small><Medal/> Earned</small>}</article>)}</div>
    </section>
    <section className="section">
      <div className="section-title"><div><span className="eyebrow">SKILLS</span><h2>What’s getting stronger</h2></div></div>
      <div className="skill-progress-card">{topSkills.length?topSkills.map(([skill,value])=><div className="kid-skill-line" key={skill}><span>{skill}</span><div><i style={{width:`${Math.round(value*100)}%`}}/></div><b>{Math.round(value*100)}%</b></div>):<div className="empty-progress"><Sparkles/><p>Finish a few missions and your skill progress will appear here.</p></div>}</div>
    </section>
    <section className="section progress-actions"><Link className="btn primary" to="/app/mission"><Award/>Start another mission</Link><Link className="btn ghost" to="/app/avatar">Change my character</Link></section>
  </div>
}